import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PenTool, Type, Eraser, Calendar as CalendarIcon, User, Building2, MapPin, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/translations';

export const SignatureSection = ({ formData, setFormData, errors, t, language }) => {
  const sigCanvasRef = useRef(null);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const sig = t.signature;
  const locale = language === 'de' ? 'de-DE' : 'en-US';

  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      signature: {
        ...prev.signature,
        [field]: value
      }
    }));
  };

  const handleSignatureEnd = () => {
    if (sigCanvasRef.current) {
      const signatureData = sigCanvasRef.current.toDataURL('image/png');
      updateField('signatureData', signatureData);
      updateField('signatureType', 'draw');
    }
  };

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
    updateField('signatureData', '');
    updateField('typedSignature', '');
  };

  const handleTypedSignature = (value) => {
    updateField('typedSignature', value);
    updateField('signatureType', 'type');
    // Generate signature-like canvas from typed text
    if (value) {
      updateField('signatureData', value);
    } else {
      updateField('signatureData', '');
    }
  };

  // Set default date to today and auto-populate from customer info
  useEffect(() => {
    if (!formData.signature.date) {
      updateField('date', new Date());
    }
    // Auto-populate from customer info if signature fields are empty
    if (!formData.signature.name && formData.customerInfo.name) {
      updateField('name', formData.customerInfo.name);
    }
    if (!formData.signature.companyName && formData.customerInfo.companyName) {
      updateField('companyName', formData.customerInfo.companyName);
    }
    if (!formData.signature.confirmationEmail && formData.customerInfo.email) {
      updateField('confirmationEmail', formData.customerInfo.email);
    }
  }, [formData.customerInfo]);

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl text-foreground">
            <PenTool className="w-5 h-5 text-primary" />
            {sig.title}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">{sig.instruction}</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Signatory Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sigName" className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                {sig.name} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="sigName"
                value={formData.signature.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder={sig.namePlaceholder}
                className={`input-focus-ring ${errors.signatureName ? 'border-destructive' : ''}`}
              />
              {errors.signatureName && (
                <p className="text-xs text-destructive">{errors.signatureName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sigCompany" className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                {sig.companyName}
              </Label>
              <Input
                id="sigCompany"
                value={formData.signature.companyName}
                onChange={(e) => updateField('companyName', e.target.value)}
                placeholder={sig.companyNamePlaceholder}
                className="input-focus-ring"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sigLocation" className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                {sig.location} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="sigLocation"
                value={formData.signature.location}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder={sig.locationPlaceholder}
                className={`input-focus-ring ${errors.signatureLocation ? 'border-destructive' : ''}`}
              />
              {errors.signatureLocation && (
                <p className="text-xs text-destructive">{errors.signatureLocation}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                {sig.date} <span className="text-destructive">*</span>
              </Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.signature.date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.signature.date 
                      ? formatDate(formData.signature.date, locale)
                      : language === 'de' ? 'Datum auswählen' : 'Pick a date'
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.signature.date}
                    onSelect={(date) => {
                      updateField('date', date);
                      setCalendarOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Signature Input */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <PenTool className="w-4 h-4 text-muted-foreground" />
              {sig.signatureLabel} <span className="text-destructive">*</span>
            </Label>

            <Tabs value={signatureMode} onValueChange={setSignatureMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="draw" className="flex items-center gap-2">
                  <PenTool className="w-4 h-4" />
                  {sig.signatureMode.draw}
                </TabsTrigger>
                <TabsTrigger value="type" className="flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  {sig.signatureMode.type}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="draw" className="mt-0">
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">{sig.drawInstruction}</p>
                  <div className={cn(
                    "relative rounded-lg border-2 border-dashed bg-card overflow-hidden",
                    errors.signature ? "border-destructive" : "border-muted-foreground/30 hover:border-primary/50"
                  )}>
                    <SignatureCanvas
                      ref={sigCanvasRef}
                      canvasProps={{
                        className: 'w-full h-48 cursor-crosshair',
                        style: { width: '100%', height: '192px' }
                      }}
                      penColor="hsl(30, 10%, 15%)"
                      onEnd={handleSignatureEnd}
                    />
                    {/* Signature line */}
                    <div className="absolute bottom-8 left-8 right-8 border-b border-muted-foreground/30" />
                    <span className="absolute bottom-3 left-8 text-xs text-muted-foreground">
                      {language === 'de' ? 'Unterschrift' : 'Signature'}
                    </span>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={clearSignature}
                    className="flex items-center gap-2"
                  >
                    <Eraser className="w-4 h-4" />
                    {sig.clear}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="type" className="mt-0">
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">{sig.typeInstruction}</p>
                  <Input
                    value={formData.signature.typedSignature}
                    onChange={(e) => handleTypedSignature(e.target.value)}
                    placeholder={sig.typePlaceholder}
                    className={cn(
                      "text-2xl font-display h-16 text-center",
                      errors.signature ? "border-destructive" : ""
                    )}
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  />
                  {formData.signature.typedSignature && (
                    <div className="p-4 rounded-lg bg-card border border-border text-center">
                      <p className="text-3xl font-display" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {formData.signature.typedSignature}
                      </p>
                      <div className="mt-2 border-t border-muted-foreground/30 pt-2">
                        <span className="text-xs text-muted-foreground">
                          {language === 'de' ? 'Digitale Unterschrift' : 'Digital Signature'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {errors.signature && (
              <p className="text-xs text-destructive">{errors.signature}</p>
            )}
          </div>

          {/* Email for PDF */}
          <div className="space-y-2 pt-4 border-t border-border">
            <Label htmlFor="confirmEmail" className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              {sig.emailConfirmation} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="confirmEmail"
              type="email"
              value={formData.signature.confirmationEmail}
              onChange={(e) => updateField('confirmationEmail', e.target.value)}
              placeholder={sig.emailPlaceholder}
              className={`input-focus-ring ${errors.confirmationEmail ? 'border-destructive' : ''}`}
            />
            {errors.confirmationEmail && (
              <p className="text-xs text-destructive">{errors.confirmationEmail}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {language === 'de' 
                ? 'Eine PDF-Kopie des unterschriebenen Vertrags wird an diese E-Mail-Adresse gesendet.'
                : 'A PDF copy of the signed contract will be sent to this email address.'
              }
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignatureSection;
